import "./repasses.scss";
import { useEffect, useMemo, useState } from "react";
import { Plus } from "lucide-react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useAppDispatch, useAppSelector } from "../../../features/helpers";
import { fetchRepasses, setModalAddStatus } from "../../../store/RepassesSlice";
import { fetchSupplier } from "../../../store/SuppliersSlice";
import { fetchAssets } from "../../../store/AssetsSlice";
import { formatAssetAmount } from "../../../utils/decimal";
import { DataList, DataListColumn, StatusBadge, FormField } from "../../../components/ui";
import { IRepasse, RepasseCategory } from "../../../models/Repasse";
import ModalAddRepasse from "../../../components/modal/modal-add-repasse/modal-add-repasse";

// "dd/mm/yyyy, hh:mm:ss" (toLocaleString do Api) -> timestamp p/ filtro de data.
const ts = (s?: string): number => {
  const m = s?.match(/(\d{2})\/(\d{2})\/(\d{4}).*?(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return 0;
  return new Date(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], +m[6]).getTime();
};
const dayStart = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
const dayEnd   = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59).getTime();

const Repasses = () => {
  const dispatch = useAppDispatch();
  const list          = useAppSelector((s) => s.repasses.list);
  const loading       = useAppSelector((s) => s.repasses.loadingResults);
  const modalStatus   = useAppSelector((s) => s.repasses.modalAddStatus);
  const suppliersList = useAppSelector((s) => s.supplier.suppliersOrganization);
  const assetsList    = useAppSelector((s) => s.assets.assets);

  const [fSupplier, setFSupplier] = useState("");
  const [fAsset, setFAsset]       = useState("");
  const [fStatus, setFStatus]     = useState("");
  const [fCategory, setFCategory] = useState<RepasseCategory | "">("");
  const [fStart, setFStart]       = useState<Date | null>(null);
  const [fEnd, setFEnd]           = useState<Date | null>(null);

  useEffect(() => {
    dispatch(fetchRepasses());
    if (!suppliersList) dispatch(fetchSupplier());
    if (!assetsList || assetsList.length === 0) dispatch(fetchAssets());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statuses = useMemo(
    () => Array.from(new Set((list ?? []).map((r) => r.status).filter(Boolean))),
    [list],
  );

  const rows = useMemo(() => {
    let r = list ?? [];
    if (fSupplier) r = r.filter((x) => String(x.supplier?.id) === fSupplier);
    if (fAsset)    r = r.filter((x) => String(x.asset?.id) === fAsset);
    if (fStatus)   r = r.filter((x) => x.status === fStatus);
    if (fCategory) r = r.filter((x) => x.category === fCategory);
    if (fStart)    r = r.filter((x) => ts(x.date) >= dayStart(fStart));
    if (fEnd)      r = r.filter((x) => ts(x.date) <= dayEnd(fEnd));
    return r;
  }, [list, fSupplier, fAsset, fStatus, fCategory, fStart, fEnd]);

  const toggle = () => dispatch(setModalAddStatus(!modalStatus));
  const clear = () => { setFSupplier(""); setFAsset(""); setFStatus(""); setFCategory(""); setFStart(null); setFEnd(null); };

  const columns: DataListColumn<IRepasse>[] = [
    {
      key: "category", header: "Tipo", width: "0.9fr",
      render: (r) => (
        <StatusBadge
          tone={r.category === "envio" ? "danger" : "success"}
          label={r.category === "envio" ? "Envio" : "Recebimento"}
        />
      ),
    },
    {
      key: "direction", header: "Direção", width: "1.6fr",
      render: (r) => {
        const sup = r.supplier?.name ?? "Fornecedor";
        return <strong>{r.category === "recebimento" ? `${sup} → Empresa` : `Empresa → ${sup}`}</strong>;
      },
    },
    {
      key: "value", header: "Valor", align: "right", mono: true,
      render: (r) => (
        <span>{formatAssetAmount(r.value, r.asset?.symbol)} <small style={{ opacity: .6 }}>{r.asset?.symbol}</small></span>
      ),
    },
    { key: "asset",  header: "Asset",  render: (r) => r.asset?.symbol ?? "—" },
    { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
    { key: "date",   header: "Data",   render: (r) => <span style={{ opacity: .55 }}>{r.date}</span>, mono: true },
  ];

  return (
    <>
      <div className="client-wrapper repasses-wrapper">

        <div className="flex sb">
          <h1 className="page-title">
            Repasses
            <small>envios (pagamentos) e recebimentos entre a empresa e os fornecedores.</small>
          </h1>
          <div className="buttons">
            <button className="btn-pill primary" onClick={toggle}><Plus size={14} /> Adicionar Repasse</button>
          </div>
        </div>

        {/* filtros */}
        <div className="repasses-filters">
          <FormField label="Fornecedor" htmlFor="f-sup">
            <select id="f-sup" value={fSupplier} onChange={(e) => setFSupplier(e.target.value)}>
              <option value="">Todos</option>
              {suppliersList?.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </FormField>
          <FormField label="Categoria" htmlFor="f-cat">
            <select id="f-cat" value={fCategory} onChange={(e) => setFCategory(e.target.value as RepasseCategory | "")}>
              <option value="">Todas</option>
              <option value="envio">Envio</option>
              <option value="recebimento">Recebimento</option>
            </select>
          </FormField>
          <FormField label="Asset" htmlFor="f-asset">
            <select id="f-asset" value={fAsset} onChange={(e) => setFAsset(e.target.value)}>
              <option value="">Todos</option>
              {assetsList?.map((a: any) => <option key={a.id} value={a.id}>{a.symbol}</option>)}
            </select>
          </FormField>
          <FormField label="Status" htmlFor="f-status">
            <select id="f-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              <option value="">Todos</option>
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </FormField>
          <FormField label="De" htmlFor="f-start">
            <DatePicker id="f-start" selected={fStart} onChange={(d: any) => setFStart(d)} dateFormat="dd/MM/yyyy" placeholderText="Início" isClearable showIcon />
          </FormField>
          <FormField label="Até" htmlFor="f-end">
            <DatePicker id="f-end" selected={fEnd} onChange={(d: any) => setFEnd(d)} dateFormat="dd/MM/yyyy" placeholderText="Fim" isClearable showIcon />
          </FormField>
          <button className="btn-pill ghost" onClick={clear}>Limpar</button>
        </div>

        <DataList
          columns={columns}
          rows={rows}
          loading={!list || loading}
          rowKey={(r: IRepasse) => `${r.category}-${r.id}`}
          empty="Sem repasses registrados."
        />
      </div>

      {modalStatus && <ModalAddRepasse />}
    </>
  );
};

export default Repasses;
